import type { BrandApp } from "@tento-ui/brand/brand-app/brand-app";
import type { PageModel, ThemeRef } from "@tento-ui/brand/page-model";
import { PageModel as PageModelParser } from "@tento-ui/brand/page-model";
import { Router } from "@tento-ui/router";

import { BrandApi } from "./brand-api";
import { extractPageModel, PageEngine } from "./page-engine";
import type { PageEnginePresetConfig } from "./page-engine-presets";
import type { PageEngineContext } from "./to-props";

export class BrandRouter {
  private onRoute: Option<(resolve: BrandApp.Config["resolve"]) => void> =
    null;
  private currentPath: Option<string> = null;
  private $pagelet: Option<HTMLElement> = null;

  constructor(private options: BrandRouter.Options) {}

  register(
    onRoute: (resolve: BrandApp.Config["resolve"]) => void,
    routes: BrandRouter.Route[],
  ): void {
    this.onRoute = onRoute;
    for (const route of routes) {
      Router.addRoute(route.pattern, (components) => {
        this.resolveFromPath(route.path(components));
      });
    }
  }

  initialResolve(path: string): BrandApp.Config["resolve"] {
    this.currentPath = path;
    return async (route) => {
      const injected = this.renderInjected(route);
      if (injected) return injected;

      if (this.options.initialPageModel) {
        return this.renderPageModel(this.options.initialPageModel, route);
      }

      const pageModel = await this.fetchPageModel(path);
      if (pageModel) return this.renderPageModel(pageModel, this.$pagelet);

      return this.options.fallback(path, route);
    };
  }

  private resolveFromPath(path: string): void {
    if (path === this.currentPath) return;
    this.currentPath = path;

    this.onRoute?.(async (route) => {
      const pageModel = await this.fetchPageModel(path);
      if (pageModel) return this.renderPageModel(pageModel, this.$pagelet);
      return this.options.fallback(path, route);
    });
  }

  private renderInjected(route: Option<HTMLElement>): Option<HTMLElement> {
    const pageModel = extractPageModel(route);
    if (!pageModel) return null;
    return this.renderPageModel(pageModel, route);
  }

  private renderPageModel(
    pageModel: PageModel,
    route: Option<HTMLElement>,
  ): HTMLElement {
    const preset = this.options.resolvePreset(pageModel.theme);
    const engine = new PageEngine(
      pageModel,
      preset.sectionSet,
      preset.preset.overrides,
      this.options.context,
    );
    this.$pagelet = engine.render(route);
    return this.$pagelet;
  }

  private async fetchPageModel(path: string): Promise<Option<PageModel>> {
    const result = await BrandApi.getResult("/api/public/brand/pages", {
      query: {
        route: path,
        query: null,
        category: null,
        limit: null,
        offset: null,
      },
    });
    const page = result.ok();
    if (!page) return null;
    return PageModelParser.parse(page);
  }
}

export namespace BrandRouter {
  export type Route = {
    pattern: string;
    path: (components: string[]) => string;
  };

  export type Fallback = (
    path: string,
    route: Option<HTMLElement>,
  ) => HTMLElement | Promise<HTMLElement>;

  export type Options = {
    context: PageEngineContext;
    initialPageModel: Option<PageModel>;
    resolvePreset: (theme: ThemeRef) => PageEnginePresetConfig;
    fallback: Fallback;
  };
}
